import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft, BookOpen, CalendarCheck, CreditCard, Edit2, GraduationCap,
  Loader2, Mail, Phone, RefreshCw, Shield, UserRound, Users, X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  attendanceApi,
  classesApi,
  feesApi,
  relationsApi,
  reportCardsApi,
  studentsApi,
  subjectsApi,
  teachersApi,
  timetableApi,
  usersApi,
} from '../api/services.js'
import { useAuth } from '../hooks/useAuth.js'
import Avatar from '../components/Avatar.jsx'
import PageHeader from '../components/PageHeader.jsx'

const getUserId = (user) => user?._id || user?.id || user?.userId
const getUserName = (user) => user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '—'
const getClassId = (item) => item?._id || item?.id || item
const getClassLabel = (item) => {
  if (!item) return 'Not assigned'
  if (typeof item === 'string') return item
  const name = item.name || item.className || 'Class'
  return item.section ? `${name} — ${item.section}` : name
}
const schoolName = localStorage.getItem('school_app_config') ? JSON.parse(localStorage.getItem('school_app_config')).name : 'Not assigned'
const getSchoolName = (user) => user?.school?.name || user?.schoolId?.name || user?.schoolName || schoolName || 'Not assigned'
const getRoleLabel = (role) => role ? role.charAt(0).toUpperCase() + role.slice(1) : 'User'
const getRoleBadge = (role) => ({
  student: 'bg-green-50 text-green-700',
  teacher: 'bg-cyan-50 text-cyan-700',
  parent: 'bg-amber-50 text-amber-700',
  admin: 'bg-violet-50 text-violet-700',
}[role] || 'bg-gray-100 text-gray-700')
const normalizeArray = (data, keys = []) => {
  if (Array.isArray(data)) return data
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key]
  }
  return []
}
const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(date)
}
const formatCurrency = (value = 0) => `₹${Number(value || 0).toLocaleString('en-IN')}`
const getSubjectClass = (subject) => subject?.classId || subject?.class || null
const getRelatedEntity = (item, keys) => {
  if (!item || typeof item !== 'object') return null
  for (const key of keys) {
    const entity = item[key]
    if (entity && typeof entity === 'object') return entity
  }
  if (item.firstName || item.lastName || item.name || item.email) return item
  return null
}
const getRelationLabel = (item) => item?.relation || item?.type || item?.relationship || ''
const getPeriodClass = (slot) => slot?.classId || slot?.class || slot?.className || null

function StatCard({ icon: Icon, label, value, tone = 'bg-gray-50 text-gray-900' }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${tone}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  )
}

function InfoCard({ title, items }) {
  return (
    <div className="card p-5">
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {items.map((item) => (
          <div key={item.label} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">{item.label}</p>
            <p className="text-sm font-medium text-gray-900 mt-2 break-words">{item.value || '—'}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

const emptyEditForm = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  gender: 'Male',
  dob: '',
  address: '',
  admissionNo: '',
  classId: '',
}

export default function UserProfilePage() {
  const { user: authUser } = useAuth()
  const { userId } = useParams()
  const resolvedUserId = userId || authUser?._id || authUser?.id
  const isOwnProfile = !userId || String(resolvedUserId) === String(authUser?._id || authUser?.id || '')
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [classes, setClasses] = useState([])
  const [studentParents, setStudentParents] = useState([])
  const [parentStudents, setParentStudents] = useState([])
  const [teacherSubjects, setTeacherSubjects] = useState([])
  const [teacherPeriods, setTeacherPeriods] = useState([])
  const [studentFeeAccounts, setStudentFeeAccounts] = useState([])
  const [reportSessions, setReportSessions] = useState([])
  const [attendanceRecords, setAttendanceRecords] = useState([])
  const [editOpen, setEditOpen] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editForm, setEditForm] = useState(emptyEditForm)

  useEffect(() => {
    console.log("user", user)
  }, [user])


  const fetchData = async () => {
    if (!resolvedUserId) {
      setUser(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setStudentParents([])
      setParentStudents([])
      setTeacherSubjects([])
      setTeacherPeriods([])
      setStudentFeeAccounts([])
      setReportSessions([])
      setAttendanceRecords([])

      const profile = await usersApi.getOne(resolvedUserId)
      setUser(profile)

      const role = profile?.role
      const tasks = []

      if (role === 'student') {
        tasks.push(
          relationsApi.getStudentParents(resolvedUserId),
          feesApi.getStudentAccount(resolvedUserId),
          reportCardsApi.getSessions(resolvedUserId),
          attendanceApi.getByStudent(resolvedUserId),
        )
        const [parentsRes, accountsRes, sessionsRes, attendanceRes] = await Promise.allSettled(tasks)
        if (parentsRes.status === 'fulfilled') setStudentParents(normalizeArray(parentsRes.value, ['parents', 'relations']))
        if (accountsRes.status === 'fulfilled') setStudentFeeAccounts(normalizeArray(accountsRes.value, ['accounts']))
        if (sessionsRes.status === 'fulfilled') setReportSessions(normalizeArray(sessionsRes.value, ['sessions']))
        if (attendanceRes.status === 'fulfilled') setAttendanceRecords(normalizeArray(attendanceRes.value, ['attendance', 'records']))
      }

      if (role === 'parent') {
        const [studentsRes] = await Promise.allSettled([relationsApi.getParentStudents(resolvedUserId)])
        if (studentsRes.status === 'fulfilled') setParentStudents(normalizeArray(studentsRes.value, ['students', 'relations']))
      }

      if (role === 'teacher') {
        const [subjectsRes, periodsRes, classesRes] = await Promise.allSettled([
          subjectsApi.getAll(),
          timetableApi.getByTeacher(resolvedUserId),
          classesApi.getAll(),
        ])

        const periods = periodsRes.status === 'fulfilled'
          ? normalizeArray(periodsRes.value, ['periods', 'slots', 'timetable'])
          : []

        if (periodsRes.status === 'fulfilled') {
          setTeacherPeriods(periods)
        }

        if (subjectsRes.status === 'fulfilled') {
          const allSubjects = normalizeArray(subjectsRes.value, ['subjects'])

          // Subject IDs from timetable — a teacher may be assigned via timetable
          // without appearing in subject.teachers[], so include both sources
          const timetableSubjectIds = new Set(
            periods
              .map((p) => {
                const sid = p.subjectId?._id || p.subjectId?.id || (typeof p.subjectId === 'string' ? p.subjectId : null)
                return sid ? String(sid) : null
              })
              .filter(Boolean)
          )

          setTeacherSubjects(
            allSubjects.filter((subject) => {
              const subjectId = String(subject._id || subject.id || '')
              const inTeachersArray = (subject.teachers || []).some(
                (t) => String(getUserId(t)) === String(resolvedUserId)
              )
              const inTimetable = subjectId && timetableSubjectIds.has(subjectId)
              return inTeachersArray || inTimetable
            })
          )
        }

        if (classesRes.status === 'fulfilled') {
          setClasses(Array.isArray(classesRes.value) ? classesRes.value : classesRes.value?.classes ?? [])
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load user profile')
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [resolvedUserId])

  const teacherClassSections = useMemo(() => {
    const labels = [
      ...teacherSubjects.map((subject) => getClassLabel(getSubjectClass(subject))),
      ...teacherPeriods.map((slot) => getClassLabel(getPeriodClass(slot))),
    ].filter(Boolean)
    return Array.from(new Set(labels))
  }, [teacherPeriods, teacherSubjects])

  const teacherClassAssignment = useMemo(() => {
    if (user?.role !== 'teacher') return null
    return classes.find((c) => {
      const ct = c.classTeacher
      if (!ct) return false
      const ctId = typeof ct === 'object' ? (ct._id || ct.id) : ct
      return String(ctId) === String(resolvedUserId)
    }) || null
  }, [classes, resolvedUserId, user?.role])

  const teacherTempAssignments = useMemo(() => {
    if (user?.role !== 'teacher') return []
    return classes.filter((c) => {
      const tmp = c.tempClassTeacher
      if (!tmp?.teacherId) return false
      const tid = typeof tmp.teacherId === 'object' ? (tmp.teacherId._id || tmp.teacherId.id) : tmp.teacherId
      return String(tid) === String(resolvedUserId)
    })
  }, [classes, resolvedUserId, user?.role])

  const attendanceSummary = useMemo(() => ({
    present: attendanceRecords.filter((record) => record.status === 'present').length,
    absent: attendanceRecords.filter((record) => record.status === 'absent').length,
    late: attendanceRecords.filter((record) => record.status === 'late').length,
  }), [attendanceRecords])

  const openEditModal = async () => {
    if (!user) return
    if (user.role === 'student' && classes.length === 0) {
      try {
        const classData = await classesApi.getAll()
        setClasses(Array.isArray(classData) ? classData : classData?.classes ?? [])
      } catch {
        toast.error('Failed to load class sections')
      }
    }

    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      mobile: user.mobile || '',
      gender: user.gender || 'Male',
      dob: user.dob || user.dateOfBirth || '',
      address: user.address || '',
      admissionNo: user.admissionNo || '',
      classId: getClassId(user.classId) || '',
    })
    setEditOpen(true)
  }

  const handleEditSave = async () => {
    if (!editForm.firstName.trim()) {
      toast.error('First name is required')
      return
    }

    try {
      setSavingEdit(true)
      const payload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        mobile: editForm.mobile,
        gender: editForm.gender,
        dob: editForm.dob || undefined,
        address: editForm.address || undefined,
      }

      if (user.role === 'student') {
        await studentsApi.update(resolvedUserId, {
          ...payload,
          admissionNo: editForm.admissionNo || undefined,
          classId: editForm.classId || undefined,
        })
      } else if (user.role === 'teacher') {
        await teachersApi.update(resolvedUserId, payload)
      } else {
        await usersApi.update(resolvedUserId, payload)
      }

      toast.success('Profile updated')
      setEditOpen(false)
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile')
    } finally {
      setSavingEdit(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="card p-8 text-center">
        <Users className="w-10 h-10 mx-auto text-gray-200 mb-3" />
        <h2 className="text-lg font-semibold text-gray-900">User not found</h2>
        <p className="text-sm text-gray-500 mt-2">The requested user profile could not be loaded.</p>
        <Link to={backLink} className="btn-secondary inline-flex items-center gap-2 mt-5">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </Link>
      </div>
    )
  }

  const displayName = getUserName(user)
  const classLabel = getClassLabel(user.classId || user.class)
  const schoolName = getSchoolName(user)
  const backLink = isOwnProfile ? '/dashboard' : '/users'
  const backLabel = isOwnProfile ? 'Back to Dashboard' : 'Back to Users'
  const roleEditLinks = user.role === 'student'
    ? [
        { to: '/parent-student-links', label: 'Manage Parent Links' },
        { to: '/fee-accounts', label: 'Manage Fees' },
        { to: `/report-cards?studentId=${resolvedUserId}`, label: 'Open Report Cards' },
      ]
    : user.role === 'teacher'
      ? [
          { to: '/subjects', label: 'Manage Subjects' },
          { to: '/manage-timetable', label: 'Manage Timetable' },
        ]
      : user.role === 'parent'
        ? [{ to: '/parent-student-links', label: 'Manage Linked Students' }]
        : [{ to: '/users', label: 'Manage Users' }]
  const contactItems = [
    { label: 'Role', value: getRoleLabel(user.role) },
    { label: 'Email', value: user.email || 'Not provided' },
    { label: 'Mobile', value: user.mobile || 'Not provided' },
    { label: 'Gender', value: user.gender || 'Not provided' },
    { label: 'School', value: schoolName },
    { label: 'Date of Birth', value: user.dob || user.dateOfBirth ? formatDate(user.dob || user.dateOfBirth) : 'Not provided' },
    { label: 'Address', value: user.address || 'Not provided' },
    { label: 'User ID', value: getUserId(user) || '—' },
    { label: 'Created', value: formatDate(user.createdAt) },
    { label: 'Last Updated', value: formatDate(user.updatedAt) },
  ]

  const roleItems = user.role === 'student'
    ? [
        { label: 'Admission No', value: user.admissionNo || 'Not provided' },
        { label: 'Class Section', value: classLabel },
        { label: 'Linked Parents', value: String(studentParents.length) },
        { label: 'Fee Accounts', value: String(studentFeeAccounts.length) },
      ]
    : user.role === 'teacher'
      ? [
          { label: 'Assigned Subjects', value: String(teacherSubjects.length) },
          { label: 'Class Sections', value: String(teacherClassSections.length) },
          { label: 'Weekly Periods', value: String(teacherPeriods.length) },
          { label: 'Profile Type', value: 'Teaching staff' },
        ]
      : user.role === 'parent'
        ? [
            { label: 'Linked Students', value: String(parentStudents.length) },
            { label: 'Primary Role', value: 'Parent / guardian' },
          ]
        : [
            { label: 'Access Level', value: 'School administrator' },
            { label: 'School', value: schoolName },
          ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={displayName}
        subtitle={isOwnProfile
          ? 'Your account details, role access, and linked operational records in the admin workspace.'
          : 'A full admin view of this account, including role details, linked records, and operational context.'}
        chips={[
          getRoleLabel(user.role),
          schoolName,
          user.role === 'student' ? classLabel : user.role === 'teacher' ? `${teacherSubjects.length} subjects` : `${user.mobile || 'No mobile'}`,
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={openEditModal} className="btn-primary inline-flex items-center gap-2">
              <Edit2 className="w-4 h-4" /> Edit Profile
            </button>
            <button onClick={fetchData} className="btn-secondary p-2" title="Refresh profile">
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link to={backLink} className="btn-secondary inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> {backLabel}
            </Link>
          </div>
        }
      />

      <div className="grid xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] gap-5">
        <div className="card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Avatar src={user.avatar} name={displayName} size="xl" />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-2xl font-bold text-gray-900">{displayName}</h3>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getRoleBadge(user.role)}`}>
                  {getRoleLabel(user.role)}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">{user.email || 'No email address added'}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {user.mobile && <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">{user.mobile}</span>}
                {user.role === 'student' && <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{classLabel}</span>}
                {user.role === 'student' && user.admissionNo && <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">{user.admissionNo}</span>}
                {user.role === 'teacher' && teacherClassAssignment && (
                  <Link
                    to={`/classes/${teacherClassAssignment._id || teacherClassAssignment.id}`}
                    className="rounded-full bg-primary-50 border border-primary-200 px-3 py-1 text-xs font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
                  >
                    Class Teacher · {teacherClassAssignment.name}{teacherClassAssignment.section ? ` — ${teacherClassAssignment.section}` : ''}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4">
          <StatCard
            icon={user.role === 'student' ? GraduationCap : user.role === 'teacher' ? BookOpen : user.role === 'parent' ? Users : Shield}
            label={user.role === 'student' ? 'Linked Parents' : user.role === 'teacher' ? 'Assigned Subjects' : user.role === 'parent' ? 'Linked Students' : 'Role'}
            value={user.role === 'student'
              ? studentParents.length
              : user.role === 'teacher'
                ? teacherSubjects.length
                : user.role === 'parent'
                  ? parentStudents.length
                  : getRoleLabel(user.role)}
            tone={user.role === 'student' ? 'bg-green-50 text-green-700' : user.role === 'teacher' ? 'bg-cyan-50 text-cyan-700' : user.role === 'parent' ? 'bg-amber-50 text-amber-700' : 'bg-violet-50 text-violet-700'}
          />
          <StatCard
            icon={UserRound}
            label={user.role === 'teacher' ? 'Class Sections' : 'School'}
            value={user.role === 'teacher' ? teacherClassSections.length : schoolName}
            tone="bg-blue-50 text-blue-700"
          />
        </div>
      </div>

      <InfoCard title="Contact & System Details" items={contactItems} />
      <InfoCard title="Role Details" items={roleItems} />

      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Edit Actions</h3>
            <p className="text-sm text-gray-500 mt-1">Admins can update the core profile here, and jump into the right module for linked records.</p>
          </div>
          <button onClick={openEditModal} className="btn-primary inline-flex items-center gap-2">
            <Edit2 className="w-4 h-4" /> Edit Profile
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          {roleEditLinks.map((item) => (
            <Link key={item.to} to={item.to} className="btn-secondary">
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      {user.role === 'student' && (
        <>
          <div className="grid lg:grid-cols-3 gap-4">
            <StatCard icon={CalendarCheck} label="Present" value={attendanceSummary.present} tone="bg-green-50 text-green-700" />
            <StatCard icon={CalendarCheck} label="Absent" value={attendanceSummary.absent} tone="bg-red-50 text-red-600" />
            <StatCard icon={CalendarCheck} label="Late" value={attendanceSummary.late} tone="bg-yellow-50 text-yellow-700" />
          </div>

          <div className="grid xl:grid-cols-2 gap-5">
            <div className="card p-5">
              <h3 className="text-lg font-bold text-gray-900">Linked Parents</h3>
              <p className="text-sm text-gray-500 mt-1">Parents and guardians connected to this student account.</p>
              <div className="space-y-3 mt-5">
                {studentParents.length > 0 ? studentParents.map((item, index) => {
                  const parent = getRelatedEntity(item, ['parentId', 'parent'])
                  if (!parent) return null
                  return (
                    <div key={getUserId(parent) || index} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar src={parent.avatar} name={getUserName(parent)} size="md" />
                        <div className="min-w-0">
                          <Link to={`/users/${getUserId(parent)}`} className="font-semibold text-gray-900 hover:text-primary-700 transition-colors block truncate">
                            {getUserName(parent)}
                          </Link>
                          <p className="text-sm text-gray-500 mt-1 truncate">{parent.email || parent.mobile || 'No contact info'}</p>
                        </div>
                      </div>
                      {getRelationLabel(item) && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">{getRelationLabel(item)}</span>}
                    </div>
                  )
                }) : (
                  <p className="text-sm text-gray-400 mt-4">No linked parents found.</p>
                )}
              </div>
            </div>

            <div className="card p-5">
              <h3 className="text-lg font-bold text-gray-900">Fee Overview</h3>
              <p className="text-sm text-gray-500 mt-1">Student fee accounts currently available in the system.</p>
              <div className="space-y-3 mt-5">
                {studentFeeAccounts.length > 0 ? studentFeeAccounts.map((account, index) => (
                  <div key={account._id || account.id || index} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">{account.academicYear || `Fee Account ${index + 1}`}</p>
                        <p className="text-sm text-gray-500 mt-1">{account.status || 'Status unavailable'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-red-600">{formatCurrency(account.outstanding || 0)}</p>
                        <p className="text-xs text-gray-400">Outstanding</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-400">Total</p>
                        <p className="font-medium text-gray-900">{formatCurrency(account.totalAmount || 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-400">Paid</p>
                        <p className="font-medium text-green-700">{formatCurrency(account.paidAmount || 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-400">Net</p>
                        <p className="font-medium text-gray-900">{formatCurrency(account.netAmount || account.totalAmount || 0)}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-sm text-gray-400 mt-4">No fee accounts found for this student.</p>
                )}
              </div>
            </div>
          </div>

            <div className="card p-5">
              <h3 className="text-lg font-bold text-gray-900">Report Card Sessions</h3>
              <p className="text-sm text-gray-500 mt-1">Published sessions available for this student.</p>
              <div className="flex flex-wrap gap-2 mt-5">
                {reportSessions.length > 0 ? reportSessions.map((session, index) => (
                <Link
                  key={session._id || session.id || session.sessionId || index}
                  to={session._id || session.id || session.sessionId
                    ? `/report-cards/${resolvedUserId}/${session._id || session.id || session.sessionId}`
                    : `/report-cards?studentId=${resolvedUserId}`}
                  className="rounded-full bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                >
                  {session.name || session.sessionName || `Session ${index + 1}`}
                </Link>
                )) : (
                  <span className="text-sm text-gray-400">No report card sessions found.</span>
                )}
            </div>
          </div>
        </>
      )}

      {user.role === 'teacher' && (
        <>
          <div className="card p-5">
            <h3 className="text-lg font-bold text-gray-900">Class Sections Covered</h3>
            <p className="text-sm text-gray-500 mt-1">Sections connected through assigned subjects and timetable entries.</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {teacherClassSections.length > 0 ? teacherClassSections.map((label) => (
                <span key={label} className="rounded-full bg-cyan-50 px-3 py-1.5 text-sm font-semibold text-cyan-700">
                  {label}
                </span>
              )) : (
                <span className="text-sm text-gray-400">No class sections linked yet.</span>
              )}
            </div>
          </div>

          <div className="grid xl:grid-cols-2 gap-5">
            <div className="card p-5">
              <h3 className="text-lg font-bold text-gray-900">Assigned Subjects</h3>
              <p className="text-sm text-gray-500 mt-1">Subjects this teacher currently handles.</p>
              <div className="space-y-3 mt-5">
                {teacherSubjects.length > 0 ? teacherSubjects.map((subject) => (
                  <div key={subject._id || subject.id} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{subject.name}</p>
                      {subject.code && <p className="text-sm text-gray-500 mt-1">{subject.code}</p>}
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 shadow-sm">
                      {getClassLabel(getSubjectClass(subject))}
                    </span>
                  </div>
                )) : (
                  <p className="text-sm text-gray-400 mt-4">No assigned subjects found.</p>
                )}
              </div>
            </div>

            <div className="card p-5">
              <h3 className="text-lg font-bold text-gray-900">Weekly Timetable Summary</h3>
              <p className="text-sm text-gray-500 mt-1">Timetable periods currently assigned to this teacher.</p>
              <div className="space-y-3 mt-5">
                {teacherPeriods.length > 0 ? teacherPeriods.map((slot, index) => (
                  <div key={slot._id || slot.id || index} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{slot.day || 'Day not set'}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {(slot.startTime && slot.endTime) ? `${slot.startTime} - ${slot.endTime}` : 'Time not set'}
                      </p>
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 shadow-sm">
                      {getClassLabel(getPeriodClass(slot))}
                    </span>
                  </div>
                )) : (
                  <p className="text-sm text-gray-400 mt-4">No timetable entries found.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {user.role === 'parent' && (
        <div className="card p-5">
          <h3 className="text-lg font-bold text-gray-900">Linked Students</h3>
          <p className="text-sm text-gray-500 mt-1">Students currently linked to this parent or guardian account.</p>
          <div className="space-y-3 mt-5">
            {parentStudents.length > 0 ? parentStudents.map((item, index) => {
              const student = getRelatedEntity(item, ['studentId', 'student'])
              if (!student) return null
              return (
                <div key={getUserId(student) || index} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar src={student.avatar} name={getUserName(student)} size="md" />
                    <div className="min-w-0">
                      <Link to={`/users/${getUserId(student)}`} className="font-semibold text-gray-900 hover:text-primary-700 transition-colors block truncate">
                        {getUserName(student)}
                      </Link>
                      <p className="text-sm text-gray-500 mt-1 truncate">
                        {student.admissionNo || 'No admission no'} {student.classId || student.class ? `• ${getClassLabel(student.classId || student.class)}` : ''}
                      </p>
                    </div>
                  </div>
                  {getRelationLabel(item) && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">{getRelationLabel(item)}</span>}
                </div>
              )
            }) : (
              <p className="text-sm text-gray-400 mt-4">No linked students found.</p>
            )}
          </div>
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-bold text-gray-900">Edit Profile</h3>
                <p className="text-xs text-gray-500 mt-0.5">Update the user details this admin can manage directly from the profile.</p>
              </div>
              <button onClick={() => setEditOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
                <input className="input-field w-full" value={editForm.firstName} onChange={(e) => setEditForm((prev) => ({ ...prev, firstName: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name</label>
                <input className="input-field w-full" value={editForm.lastName} onChange={(e) => setEditForm((prev) => ({ ...prev, lastName: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                <input className="input-field w-full" value={editForm.email} onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mobile</label>
                <input className="input-field w-full" value={editForm.mobile} onChange={(e) => setEditForm((prev) => ({ ...prev, mobile: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Gender</label>
                <select className="input-field w-full" value={editForm.gender} onChange={(e) => setEditForm((prev) => ({ ...prev, gender: e.target.value }))}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Date of Birth</label>
                <input type="date" className="input-field w-full" value={editForm.dob} onChange={(e) => setEditForm((prev) => ({ ...prev, dob: e.target.value }))} />
              </div>

              {user.role === 'student' && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Admission No</label>
                    <input className="input-field w-full" value={editForm.admissionNo} onChange={(e) => setEditForm((prev) => ({ ...prev, admissionNo: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Class Section</label>
                    <select className="input-field w-full" value={editForm.classId} onChange={(e) => setEditForm((prev) => ({ ...prev, classId: e.target.value }))}>
                      <option value="">Select class section</option>
                      {classes.map((classItem) => (
                        <option key={getClassId(classItem)} value={getClassId(classItem)}>
                          {getClassLabel(classItem)}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Address</label>
                <textarea className="input-field w-full h-20 resize-none" value={editForm.address} onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value }))} />
              </div>
            </div>

            <div className="px-6 pb-6 flex items-center justify-end gap-3">
              <button onClick={() => setEditOpen(false)} className="btn-secondary" disabled={savingEdit}>Cancel</button>
              <button onClick={handleEditSave} className="btn-primary flex items-center gap-2" disabled={savingEdit}>
                {savingEdit && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
